/** Диалог «Создать заказ на перемещение» — ЧИСТАЯ модель.
 *
 *  Всё, что человек видит перед нажатием (склад, источник, горизонт, сколько строк и штук,
 *  сам список строк) и всё, что он видит после отказа (какие строки не влезли в остаток
 *  источника), считается здесь — без React и без сети. Экран только рисует.
 *
 *  Без молчаливых фоллбэков: пустой список не отправляется («везти нечего» — это отказ с текстом,
 *  а не пустой документ в 1С), а отказ 409 разбирается в СТРОКИ: тост «конфликт» не говорит
 *  менеджеру, что именно уменьшить.
 */
import type { FbsReport, FbsTransferLine, FbsTransferOverLine } from "@/api/supply";
import { linesToSend } from "./fbsEdit";
import type { FbsEdits } from "./fbsEdit";

/** Строка подтверждения: то же, что уедет в документ, плюс имя и артикул для глаз. */
export interface FbsSubmitLine extends FbsTransferLine {
  article: string;
  nom_name: string;
}

/** Сводка диалога: шапка документа и его строки. */
export interface FbsSubmitSummary {
  warehouseName: string;
  sourceName: string;
  horizonDays: number;
  linesCount: number;
  qtyTotal: number;
  lines: FbsSubmitLine[];
}

/** Строка, из которой может уехать количество: расчётная (`rows`) или «без продаж ФБС». */
export interface FbsSendRow {
  nom_key: string;
  recommended: number;
  article: string;
  nom_name: string;
}

/** Всё, что может уехать в заказ: строки расчёта, затем «Без продаж ФБС» с рекомендацией 0 —
 *  у них количество только ручное (правка в тех же `edits`). Номенклатура в обоих списках сразу —
 *  нарушение контракта бэка (блок «без продаж» = товары БЕЗ строки в таблице): молча сложить
 *  или выбрать одну — значит увезти не то, что человек видит, поэтому исключение. */
export function sendableRows(
  report: Pick<FbsReport, "rows" | "no_sales_rows">,
): FbsSendRow[] {
  const main = new Set(report.rows.map((r) => r.nom_key));
  const dup = report.no_sales_rows.filter((r) => main.has(r.nom_key));
  if (dup.length > 0)
    throw new Error(
      `Отчёт ФБС: номенклатура и в таблице, и в «Без продаж ФБС» (${dup
        .map((r) => r.article || r.nom_key)
        .join(", ")}) — заказ собрать нельзя`,
    );
  return [
    ...report.rows,
    ...report.no_sales_rows.map((r) => ({
      nom_key: r.nom_key,
      recommended: 0,
      article: r.article,
      nom_name: r.nom_name,
    })),
  ];
}

/** Что уедет в 1С по текущему отчёту и правкам «К перевозу»: только строки с qty > 0.
 *
 *  Сами строки считает `fbsEdit.linesToSend` — второго счёта «что уедет» быть не должно, иначе
 *  сводка диалога и таблица разойдутся. Здесь к ним добавляются артикул и имя для глаз.
 *  Порядок — как в отчёте (он отсортирован по потребности), чтобы документ читался сверху вниз
 *  так же, как таблица на экране.
 */
export function buildSummary(
  report: FbsReport,
  rows: readonly FbsSendRow[],
  edits: FbsEdits,
): FbsSubmitSummary {
  const byKey = new Map(rows.map((r) => [r.nom_key, r]));
  const lines: FbsSubmitLine[] = linesToSend(rows, edits).map((l) => ({
    ...l,
    article: byKey.get(l.nom_key)?.article ?? "",
    nom_name: byKey.get(l.nom_key)?.nom_name ?? "",
  }));
  return {
    warehouseName: report.warehouse.name,
    sourceName: report.warehouse.source_wh_name,
    horizonDays: report.params.horizon_days,
    linesCount: lines.length,
    qtyTotal: lines.reduce((sum, l) => sum + l.qty, 0),
    lines,
  };
}

/** Почему отправлять нельзя, либо `null`. Проверяем то же, что бэк, но ДО похода в 1С —
 *  чтобы кнопка объясняла отказ, а не ловила его ответом. */
export function submitBlockedReason(summary: FbsSubmitSummary): string | null {
  if (summary.linesCount === 0)
    return "Ни одной строки с количеством больше нуля — везти нечего. Проставьте «К перевозу».";
  if (summary.linesCount > 200)
    return `Строк ${summary.linesCount}: больше 200 за раз не везём — отправьте частями.`;
  if (summary.lines.some((l) => !Number.isSafeInteger(l.qty) || l.qty <= 0))
    return "Есть строка с нецелым или неположительным количеством.";
  return null;
}

/** Тело запроса из сводки (артикул и имя — только для экрана, в 1С они не уезжают). */
export function toRequestLines(summary: FbsSubmitSummary): FbsTransferLine[] {
  return summary.lines.map((l) => ({ nom_key: l.nom_key, qty: l.qty }));
}

/** Разобранный отказ создания. `over` непуст только у 409 «не хватает на источнике». */
export interface FbsSubmitFailure {
  message: string;
  over: FbsTransferOverLine[];
  /** 500 после POST'а: документ в 1С УЖЕ создан, повторять нельзя. */
  documentExists: boolean;
}

interface ErrorShape {
  response?: { status?: number; data?: { detail?: unknown } };
  message?: string;
}

function isOverLine(v: unknown): v is FbsTransferOverLine {
  const o = v as FbsTransferOverLine;
  return (
    typeof o === "object" &&
    o !== null &&
    typeof o.nom_key === "string" &&
    typeof o.qty === "number" &&
    typeof o.source_free === "number"
  );
}

/** Отказ создания → текст + строки превышения. 409 приходит объектом `{message, over}`;
 *  всё остальное (422 pydantic, 502, 500) сводится к тексту, который и показываем. */
export function parseSubmitError(err: unknown, fallback = "Заказ не создан"): FbsSubmitFailure {
  const e = err as ErrorShape;
  const status = e?.response?.status;
  const detail = e?.response?.data?.detail;

  if (detail !== null && typeof detail === "object" && !Array.isArray(detail)) {
    const d = detail as { message?: unknown; over?: unknown };
    const over = Array.isArray(d.over) ? d.over.filter(isOverLine) : [];
    return {
      message: typeof d.message === "string" ? d.message : fallback,
      over,
      // признак «документ мог быть создан» — сам код 500, а не форма тела: придёт он объектом
      // (как 409), и предупреждение «не повторяйте» потерялось бы молча
      documentExists: status === 500,
    };
  }

  let message = fallback;
  if (typeof detail === "string") message = detail;
  else if (Array.isArray(detail))
    message =
      detail
        .map((d) => {
          const item = d as { loc?: unknown[]; msg?: string };
          const field = Array.isArray(item.loc)
            ? item.loc.filter((x) => x !== "body").join(".")
            : "";
          return field ? `${field} — ${item.msg ?? ""}` : (item.msg ?? "");
        })
        .filter(Boolean)
        .join("; ") || fallback;
  else if (typeof e?.message === "string") message = e.message;

  // 500 отдаётся ровно в одном случае: документ уже в 1С, а цикл не закрылся
  return { message, over: [], documentExists: status === 500 };
}
